import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useClinicOnboarding } from '../../hooks/useClinicOnboarding';
import { useNavCounts } from '../../hooks/useNavCounts';
import { Link, useLocation, Outlet, useNavigate, Navigate } from 'react-router-dom';
import {
  UsersRound,
  Megaphone,
  Cog,
  LogOut,
  Menu,
  Wand2,
  FileDown,
  X,
  ChevronDown,
  User,
  Upload,
} from 'lucide-react';

const workflowItems = [
  { to: '/patients', icon: UsersRound, label: 'Patients', badgeKey: 'toContact' },
  { to: '/campaigns', icon: Megaphone, label: 'Campaigns', badgeKey: 'activeCampaigns' },
];

const configureItems = [
  { to: '/automations', icon: Wand2, label: 'Automations' },
  { to: '/import-history', icon: FileDown, label: 'Imports' },
  { to: '/settings', icon: Cog, label: 'Settings' },
];

const allNavItems = [...workflowItems, ...configureItems];

// Breadcrumb config per route
const breadcrumbMap = {
  '/patients': { label: "Today's Outreach" },
  '/patients/engaged': { label: 'Engaged' },
  '/campaigns': { label: 'Campaigns' },
  '/campaigns/new': { parent: 'Campaigns', child: 'New Campaign' },
  '/automations': { label: 'Automations' },
  '/import-history': { label: 'Imports' },
  '/settings': { label: 'Settings' },
};

export default function AppLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { hasCompletedOnboarding, loading: onboardingLoading } = useClinicOnboarding();
  const { patientCounts, activeCampaigns } = useNavCounts();

  useEffect(() => {
    const stored = localStorage.getItem('sidebarCollapsed');
    if (stored === 'true') setCollapsed(true);
  }, []);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!user) return null;

  const handleLogout = async () => {
    setIsUserMenuOpen(false);
    if (logout) {
      try {
        await logout();
        navigate('/login');
      } catch (error) {
        console.error("Logout failed:", error);
      }
    }
  };

  const toggleSidebar = () => {
    if (window.innerWidth < 768) {
      setIsMobileSidebarOpen(!isMobileSidebarOpen);
    } else {
      const newVal = !collapsed;
      setCollapsed(newVal);
      localStorage.setItem('sidebarCollapsed', newVal ? 'true' : 'false');
    }
  };

  const closeMobileSidebar = () => {
    setIsMobileSidebarOpen(false);
  };

  const isActive = (path) => {
    return location.pathname === path ||
           (location.pathname.startsWith(path) && path !== '/') ||
           (location.pathname === '/' && path === '/dashboard');
  };

  // Badge values lookup
  const getBadgeValue = (item) => {
    if (!item.badgeKey) return 0;
    if (item.badgeKey === 'toContact') return patientCounts.toContact;
    if (item.badgeKey === 'activeCampaigns') return activeCampaigns;
    return 0;
  };

  const getBadgeColor = (item) => {
    if (item.badgeKey === 'toContact') return 'bg-rose-500';
    if (item.badgeKey === 'activeCampaigns') return 'bg-indigo-500';
    return 'bg-slate-500';
  };

  // Breadcrumb
  const breadcrumb = breadcrumbMap[location.pathname];
  const userInitial = user?.email?.charAt(0).toUpperCase() || 'U';
  const userEmail = user?.email || 'User';

  // Hide sidebar during onboarding - users must upload first
  const showSidebar = hasCompletedOnboarding === true;

  // Render a nav item
  const renderNavItem = (item) => {
    const Icon = item.icon;
    const active = isActive(item.to);
    const badgeValue = getBadgeValue(item);
    const badgeColor = getBadgeColor(item);

    return (
      <Link
        key={item.to}
        to={item.to}
        onClick={closeMobileSidebar}
        className={`
          relative flex items-center gap-3 px-3 py-2.5 rounded-lg
          transition-colors duration-150
          ${active
            ? 'bg-slate-800 text-white'
            : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
          }
          ${collapsed ? 'justify-center' : ''}
        `}
        title={collapsed ? item.label : undefined}
      >
        <div className="relative flex-shrink-0">
          <Icon className="h-5 w-5" strokeWidth={1.5} />
          {collapsed && badgeValue > 0 && (
            <span className={`absolute -top-1 -right-1 h-2 w-2 rounded-full ${badgeColor}`} />
          )}
        </div>
        {!collapsed && (
          <>
            <span className="text-sm font-medium">{item.label}</span>
            {badgeValue > 0 && (
              <span className={`ml-auto h-5 min-w-[20px] px-1.5 inline-flex items-center justify-center text-[11px] font-semibold text-white rounded-full ${badgeColor}`}>
                {badgeValue}
              </span>
            )}
          </>
        )}
      </Link>
    );
  };

  // Show loading state while checking onboarding status
  if (onboardingLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="h-8 w-8 border-2 border-slate-200 border-t-slate-600 rounded-full animate-spin" />
      </div>
    );
  }

  // Minimal layout for onboarding (no sidebar, just logout)
  if (!showSidebar) {
    // Force all routes to /patients during onboarding
    if (location.pathname !== '/patients') {
      return <Navigate to="/patients" replace />;
    }

    return (
      <div className="min-h-screen bg-slate-50">
        {/* Minimal header with logout */}
        <header className="absolute top-0 right-0 p-4 z-10">
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-3 py-2 text-sm text-slate-500 hover:text-slate-700 hover:bg-white/50 rounded-lg transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Logout
          </button>
        </header>
        <Outlet />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-40 flex flex-col bg-slate-900
          transform transition-all duration-200 ease-in-out
          md:relative md:translate-x-0
          ${collapsed ? 'md:w-[72px]' : 'md:w-60'}
          ${isMobileSidebarOpen ? 'translate-x-0 w-60' : '-translate-x-full'}
        `}
      >
        {/* Logo */}
        <div className={`flex items-center h-12 px-4 border-b border-slate-800 ${collapsed ? 'justify-center' : ''}`}>
          <div className="flex items-center gap-2.5">
            <div className="h-7 w-7 rounded-lg bg-indigo-600 flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-xs">O</span>
            </div>
            {!collapsed && (
              <span className="text-white font-semibold text-base tracking-tight">Outreach</span>
            )}
          </div>
          {/* Mobile close button */}
          <button
            onClick={closeMobileSidebar}
            className="ml-auto p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 md:hidden"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 overflow-y-auto">
          {/* Workflow section */}
          {!collapsed && (
            <p className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              Workflow
            </p>
          )}
          <div className="space-y-1">
            {workflowItems.map(renderNavItem)}
          </div>

          {/* Configure section */}
          <div className={collapsed ? 'mt-4' : 'mt-6'}>
            {!collapsed && (
              <p className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                Configure
              </p>
            )}
            <div className="space-y-1">
              {configureItems.map(renderNavItem)}
            </div>
          </div>
        </nav>
      </aside>

      {/* Mobile overlay */}
      {isMobileSidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900/50 z-30 md:hidden"
          onClick={closeMobileSidebar}
        />
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="sticky top-0 z-20 flex items-center justify-between h-16 px-4 bg-white border-b border-slate-200">
          <div className="flex items-center gap-3">
            {/* Mobile-only hamburger */}
            <button
              onClick={toggleSidebar}
              className="p-2 -ml-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors md:hidden"
              aria-label="Toggle sidebar"
            >
              <Menu className="h-5 w-5" />
            </button>

            {/* Breadcrumb */}
            <div className="flex items-center">
              {breadcrumb?.parent ? (
                <div className="flex items-center gap-1.5 text-sm">
                  <Link to={allNavItems.find(n => n.label === breadcrumb.parent)?.to || '#'} className="text-slate-500 hover:text-slate-700">
                    {breadcrumb.parent}
                  </Link>
                  <span className="text-slate-300">/</span>
                  <span className="font-semibold text-slate-900">{breadcrumb.child}</span>
                </div>
              ) : (
                <h1 className="text-lg font-semibold text-slate-900">
                  {breadcrumb?.label || location.pathname.split('/').filter(Boolean).pop()?.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Dashboard'}
                </h1>
              )}
            </div>
          </div>

          {/* Right side - Import button + User menu */}
          <div className="flex items-center gap-3">
            {/* Import button */}
            <button
              onClick={() => navigate('/import-history')}
              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 text-slate-600 hover:bg-slate-50 rounded-lg transition-colors"
            >
              <Upload className="h-4 w-4" strokeWidth={1.5} />
              Import
            </button>

            {/* User dropdown */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <div className="h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center">
                  <span className="text-sm font-medium text-slate-600">
                    {userInitial}
                  </span>
                </div>
                <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform duration-200 ${isUserMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown menu */}
              {isUserMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl border border-slate-200 shadow-lg py-1 z-50">
                  {/* User info */}
                  <div className="px-4 py-3 border-b border-slate-100">
                    <p className="text-sm font-medium text-slate-900 truncate">{userEmail}</p>
                    <p className="text-xs text-slate-500 mt-0.5">Clinic Admin</p>
                  </div>

                  {/* Menu items */}
                  <div className="py-1">
                    <Link
                      to="/settings"
                      onClick={() => setIsUserMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                    >
                      <User className="h-4 w-4 text-slate-400" />
                      Profile Settings
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors w-full"
                    >
                      <LogOut className="h-4 w-4 text-slate-400" />
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-6 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
